import type {
  CompanyAuthorizationRole,
  CompanyType,
} from "./company-context.js";

export const companyStatuses = [
  "new",
  "incomplete",
  "pending_review",
  "verified",
  "rejected",
  "blocked",
  "archived",
] as const;

export type CompanyStatus = (typeof companyStatuses)[number];

export const editableCompanyStatuses = [
  "new",
  "incomplete",
  "rejected",
] as const satisfies readonly CompanyStatus[];

export const operationalCompanyStatuses = [
  "verified",
] as const satisfies readonly CompanyStatus[];

export const suspendedCompanyStatuses = [
  "blocked",
  "archived",
] as const satisfies readonly CompanyStatus[];

export interface CompanyOwnerRoleMap {
  client: "client_owner";
  vendor: "vendor_owner";
}

export interface CompanyManagerRoleMap {
  client: "client_manager";
  vendor: "vendor_manager";
}

export const companyOwnerRoles: CompanyOwnerRoleMap = {
  client: "client_owner",
  vendor: "vendor_owner",
};

export const companyManagerRoles: CompanyManagerRoleMap = {
  client: "client_manager",
  vendor: "vendor_manager",
};

export const companyInvitationRoles = [
  "client_manager",
  "vendor_manager",
] as const satisfies readonly CompanyAuthorizationRole[];

export type CompanyInvitationRole = (typeof companyInvitationRoles)[number];

export const companyOwnerRegistrationTypes = [
  "client_owner",
  "vendor_owner",
] as const;

export type CompanyOwnerRegistrationType =
  (typeof companyOwnerRegistrationTypes)[number];

export const companyTypesByOwnerRegistrationType = {
  client_owner: "client",
  vendor_owner: "vendor",
} as const satisfies Record<CompanyOwnerRegistrationType, CompanyType>;

export function ownerRoleForCompanyType<TType extends CompanyType>(
  companyType: TType,
): CompanyOwnerRoleMap[TType] {
  return companyOwnerRoles[companyType];
}

export function managerRoleForCompanyType<TType extends CompanyType>(
  companyType: TType,
): CompanyManagerRoleMap[TType] {
  return companyManagerRoles[companyType];
}

export function companyTypeForOwnerRegistration(
  registrationType: CompanyOwnerRegistrationType,
): CompanyType {
  return companyTypesByOwnerRegistrationType[registrationType];
}

export function isCompanyOwnerRegistrationType(
  value: string,
): value is CompanyOwnerRegistrationType {
  return (companyOwnerRegistrationTypes as readonly string[]).includes(value);
}

export function isCompanyInvitationRole(
  value: string,
): value is CompanyInvitationRole {
  return (companyInvitationRoles as readonly string[]).includes(value);
}

export function isCompanyStatus(value: string): value is CompanyStatus {
  return (companyStatuses as readonly string[]).includes(value);
}

export function isEditableCompanyStatus(status: CompanyStatus): boolean {
  return (editableCompanyStatuses as readonly CompanyStatus[]).includes(status);
}

export function isOperationalCompanyStatus(status: CompanyStatus): boolean {
  return (operationalCompanyStatuses as readonly CompanyStatus[]).includes(status);
}

export function isSuspendedCompanyStatus(status: CompanyStatus): boolean {
  return (suspendedCompanyStatuses as readonly CompanyStatus[]).includes(status);
}

export function isOwnerRoleForCompanyType(
  role: CompanyAuthorizationRole,
  companyType: CompanyType,
): boolean {
  return role === ownerRoleForCompanyType(companyType);
}

export function isInvitationRoleForCompanyType(
  role: CompanyInvitationRole,
  companyType: CompanyType,
): boolean {
  return role === managerRoleForCompanyType(companyType);
}
